import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { Button } from "../../../Components/Button";
import { getAdminLogs } from "../../../DataService/AdminLogService";
import { AdminLogEntryDto, AdminLogLevelFilter, AdminLogPageDto } from "../../../Types/AdminLogTypes";
import "../AdminPortalTools.css";
import "../AdminLogsPanel.css";

const LEVELS: { value: AdminLogLevelFilter; label: string }[] = [
  { value: "all", label: "Alla nivåer" },
  { value: "Verbose", label: "Verbose" },
  { value: "Debug", label: "Debug" },
  { value: "Information", label: "Information" },
  { value: "Warning", label: "Warning" },
  { value: "Error", label: "Error" },
  { value: "Fatal", label: "Fatal" },
];

const pageSizeOptions = [25, 50, 100, 200];

const formatTimestamp = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("sv-SE");
};

const levelTone = (level: string) => `admin-log-badge level-${level.toLowerCase()}`;

const AdminLogRow = ({ log }: { log: AdminLogEntryDto }) => (
  <details className="admin-log-row">
    <summary>
      <span className={levelTone(log.level)}>{log.level}</span>
      <span className="admin-log-timestamp">{formatTimestamp(log.timeStamp)}</span>
      <span className="admin-log-message">{log.message}</span>
    </summary>
    <div className="admin-log-details">
      <div>
        <span className="admin-log-field-label">Meddelandemall</span>
        <p>{log.messageTemplate || "—"}</p>
      </div>
      {log.exception && (
        <div>
          <span className="admin-log-field-label">Exception</span>
          <pre>{log.exception}</pre>
        </div>
      )}
      {log.properties && (
        <div>
          <span className="admin-log-field-label">Properties</span>
          <pre>{log.properties}</pre>
        </div>
      )}
    </div>
  </details>
);

export const AdminLogsPanel = () => {
  const [data, setData] = useState<AdminLogPageDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [level, setLevel] = useState<AdminLogLevelFilter>("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const summary = useMemo(() => {
    if (!data) return "Inga loggar laddade ännu.";
    const start = data.totalCount === 0 ? 0 : (data.page - 1) * data.pageSize + 1;
    const end = Math.min(data.page * data.pageSize, data.totalCount);
    return `Visar ${start}-${end} av ${data.totalCount}`;
  }, [data]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const load = async () => {
        setLoading(true);
        setError(null);

        try {
          const result = await getAdminLogs({ page, pageSize, level, search, from: from || undefined, to: to || undefined });
          setData(result);
        } catch {
          setError("Kunde inte läsa loggarna just nu.");
          setData(null);
          toast.error("Kunde inte läsa loggarna.");
        } finally {
          setLoading(false);
        }
      };

      void load();
    }, 250);

    return () => window.clearTimeout(timer);
  }, [page, pageSize, level, search, from, to]);

  return (
    <section className="admin-surface-card admin-campain-main-card admin-tools-panel admin-logs-panel">
      <div className="admin-tools-section-title">
        <div>
          <h2>Systemloggar</h2>
          <p>Affärskritiska loggar från köp, callbacks, användare och bakgrundstjänster.</p>
        </div>
        <div className="admin-logs-summary">{summary}</div>
      </div>

      <div className="admin-logs-filters">
        <label>
          <span>Sök</span>
          <input
            className="admin-tools-input"
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            placeholder="Sök i meddelande, mall, properties..."
          />
        </label>

        <label>
          <span>Nivå</span>
          <select
            className="admin-tools-input"
            value={level}
            onChange={(e) => {
              setPage(1);
              setLevel(e.target.value as AdminLogLevelFilter);
            }}
          >
            {LEVELS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Från</span>
          <input
            className="admin-tools-input"
            type="date"
            value={from}
            onChange={(e) => {
              setPage(1);
              setFrom(e.target.value);
            }}
          />
        </label>

        <label>
          <span>Till</span>
          <input
            className="admin-tools-input"
            type="date"
            value={to}
            onChange={(e) => {
              setPage(1);
              setTo(e.target.value);
            }}
          />
        </label>

        <label>
          <span>Per sida</span>
          <select
            className="admin-tools-input"
            value={pageSize}
            onChange={(e) => {
              setPage(1);
              setPageSize(Number(e.target.value));
            }}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <p>Laddar loggar...</p>
      ) : error ? (
        <div className="admin-logs-empty">
          <h3>Vi kunde inte läsa loggarna</h3>
          <p>{error}</p>
        </div>
      ) : !data || data.items.length === 0 ? (
        <div className="admin-logs-empty">
          <h3>Inga loggar matchar filtret</h3>
          <p>Justera sökningen eller tidsintervallet.</p>
        </div>
      ) : (
        <div className="admin-logs-list">
          {data.items.map((log) => (
            <AdminLogRow key={log.id} log={log} />
          ))}
        </div>
      )}

      <div className="admin-logs-pagination">
        <Button className="basic-button admin-logs-pagination-button" text="← Föregående" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading} />
        <div className="admin-logs-pagination-status" aria-label="Sidinformation">
          <span>Aktuell sida</span>
          <strong>
            {data?.page ?? page}
            <em>av {data?.totalPages ?? 0}</em>
          </strong>
        </div>
        <Button
          className="basic-button admin-logs-pagination-button"
          text="Nästa →"
          onClick={() => setPage((p) => Math.min(data?.totalPages || p + 1, p + 1))}
          disabled={loading || !data || page >= data.totalPages}
        />
      </div>
    </section>
  );
};
